import React, { useState } from 'react';
import { Box, Divider, FormControlLabel, Typography } from '@mui/material';
import { FlexBox, FlexBoxBetween } from '../../../../components/tableItems';
import {
  ApproveRejectButton,
  MainButton,
} from '../../../../components/buttons';
import { AliasFormInterface } from '../AliasConstants';
import BoxAddSettings from '../../../../components/Forms/BoxAddSettings';
import {
  SelectFormInput,
  TextFormField,
} from '../../../../components/Forms/FormInputs';
import { BasicArray } from '../../../../models/General';
import { DefaultCheckbox } from '../../../../components/inputs';
import AliasConfirmation from './AliasConfirmation';
import AliasSendTest from './AliasSendTest';

interface AliasFormProps {
  type: 'view' | 'add';
  closeDrawer: () => void;
  sites: { id: number; name: string }[];
  users: { id: number; name: string }[];
  aliasFormObject: AliasFormInterface;
  setAliasFormObject: React.Dispatch<React.SetStateAction<AliasFormInterface>>;
  resetForm: () => void;
  loadingSubmit: boolean;
  submitNewAlias: () => void;
  updateAlias: (obj: AliasFormInterface) => Promise<void>;
  sendTestAlias: () => Promise<void>;
}

const AliasForm: React.FC<AliasFormProps> = ({
  type,
  closeDrawer,
  sites,
  users,
  aliasFormObject,
  setAliasFormObject,
  resetForm,
  loadingSubmit,
  submitNewAlias,
  updateAlias,
  sendTestAlias,
}) => {
  const [aliasActive, setAliasActive] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const openConfirmationModal = () => setConfirmationModal(true);
  const closeConfirmationModal = () => setConfirmationModal(false);

  const [modalSendTest, setModalSendTest] = useState(false);
  const openModalSendTest = () => setModalSendTest(true);
  const closeModalSendTest = () => setModalSendTest(false);

  const checkSave = () => {
    return (
      aliasFormObject.user_id !== 0 &&
      aliasFormObject.smtp_alias.trim() !== '' &&
      aliasFormObject.site_id !== 0
    );
  };

  const confirmAction = async () => {
    await updateAlias({ ...aliasFormObject, is_active: aliasActive });
    closeConfirmationModal();
  };

  const confirmSendTest = async () => {
    await sendTestAlias();
    closeModalSendTest();
  };

  return (
    <>
      <FlexBoxBetween padding="15px 25px">
        <Typography fontWeight={700} fontSize={18} textTransform="capitalize">
          {type === 'add' ? 'Create' : 'View'} Alias
        </Typography>
        <FlexBox>
          <MainButton
            onClick={() => {
              closeDrawer();
              resetForm();
            }}
            disabled={loadingSubmit}
            variant="contained"
            sx={{
              backgroundColor: '#FFFFFF',
              color: 'black',
              borderColor: 'grey',
              border: 1,
              '&:hover': {
                backgroundColor: '#FFFFFF',
              },
            }}
          >
            Cancel
          </MainButton>
          {type === 'add' && (
            <MainButton
              loading={loadingSubmit}
              variant="contained"
              sx={{
                marginX: 1.5,
                background: '#BAF372',
                color: 'black',
                '&:hover': {
                  backgroundColor: '#BAF372',
                },
              }}
              disabled={!checkSave() || loadingSubmit}
              onClick={submitNewAlias}
            >
              Save
            </MainButton>
          )}

          {type === 'view' && (
            <MainButton
              loading={false}
              variant="contained"
              sx={{
                marginX: 1.5,
                background: '#BAF372',
                color: 'black',
                '&:hover': {
                  backgroundColor: '#BAF372',
                },
              }}
              disabled={loadingSubmit}
              onClick={openModalSendTest}
            >
              Send Test
            </MainButton>
          )}
        </FlexBox>
      </FlexBoxBetween>
      <Divider />
      <BoxAddSettings title="Alias Info">
        <SelectFormInput
          required
          clearIcon
          options={users}
          disabled={users.length === 0 || type === 'view'}
          placeholder={users.length === 0 ? 'Loading users...' : 'User'}
          value={
            users.find(item => item.id === aliasFormObject.user_id) ?? {
              id: 0,
              name: '',
            }
          }
          onChange={(value: BasicArray | null) => {
            setAliasFormObject({
              ...aliasFormObject,
              user_id: value ? (value.id as number) : 0,
            });
          }}
        />
        <Box mt={3.3}>
          <SelectFormInput
            required
            options={sites}
            disabled={sites.length === 0 || type === 'view'}
            placeholder={sites.length === 0 ? 'Loading sites...' : 'Site'}
            value={
              sites.find(item => item.id === aliasFormObject.site_id) ?? {
                id: 0,
                name: '',
              }
            }
            onChange={(value: BasicArray | null) => {
              setAliasFormObject({
                ...aliasFormObject,
                site_id: value ? (value.id as number) : 0,
              });
            }}
          />
        </Box>
        <Box mt={3.3}>
          <TextFormField
            disabled={type === 'view'}
            nameInput="alias"
            required
            label="Alias"
            error={false}
            value={aliasFormObject.smtp_alias}
            errorText=""
            placeholder="Alias"
            onChange={(value: string) => {
              if (!value.includes('@'))
                setAliasFormObject({ ...aliasFormObject, smtp_alias: value });
            }}
          />
          {type === 'add' && (
            <Typography variant="caption" color="gray" fontWeight={600}>
              {aliasFormObject.smtp_alias.trim() !== '' &&
                aliasFormObject.site_id !== 0 &&
                `${aliasFormObject.smtp_alias}@${sites
                  .find(item => item.id === aliasFormObject.site_id)
                  ?.name.toLowerCase()}`}
            </Typography>
          )}
        </Box>

        <Box mt={3.3}>
          <FormControlLabel
            label="Is Active"
            sx={{
              '& .MuiFormControlLabel-label': {
                fontSize: 14,
                color: '#000000',
              },
            }}
            control={
              <DefaultCheckbox
                disabled={type === 'view'}
                id="is_active"
                name="is_active"
                size="medium"
                checked={aliasFormObject.is_active}
                onClick={() =>
                  setAliasFormObject({
                    ...aliasFormObject,
                    is_active: !aliasFormObject.is_active,
                  })
                }
              />
            }
          />
        </Box>
      </BoxAddSettings>
      {type === 'view' && (
        <Box
          className="drawer-width"
          sx={{
            background: 'white',
            position: 'fixed',
            bottom: 0,
            paddingY: 18,
          }}
        >
          <Box display="flex" justifyContent="center" alignItems="center">
            <FlexBox>
              <ApproveRejectButton
                onClick={() => {
                  setAliasActive(false);
                  openConfirmationModal();
                }}
                disabled={!aliasFormObject.is_active || loadingSubmit}
                sx={{
                  background: '#E10000',
                  marginRight: 2,
                  '&:hover': { background: '#F40F0F' },
                  '&:disabled': { background: 'gray' },
                }}
              >
                Deactivate
              </ApproveRejectButton>
              <ApproveRejectButton
                onClick={() => {
                  setAliasActive(true);
                  openConfirmationModal();
                }}
                disabled={aliasFormObject.is_active || loadingSubmit}
                sx={{
                  background: '#5CB85C',
                  '&:hover': { background: '#69C969' },
                  '&:disabled': { background: 'gray' },
                }}
              >
                Activate
              </ApproveRejectButton>
            </FlexBox>
          </Box>
        </Box>
      )}

      <AliasConfirmation
        closeConfirmationModal={closeConfirmationModal}
        confirmAction={confirmAction}
        confirmationModal={confirmationModal}
        option={aliasActive}
        isLoading={loadingSubmit}
      />

      <AliasSendTest
        closeConfirmationModal={closeModalSendTest}
        confirmAction={confirmSendTest}
        confirmationModal={modalSendTest}
        isLoading={loadingSubmit}
      />
    </>
  );
};

export default AliasForm;
